import { useEffect, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import { X } from 'lucide-react'

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
}

export function Button({ variant = 'secondary', size = 'md', className, ...rest }: ButtonProps) {
  const styles = {
    primary: 'bg-accent text-accent-fg hover:opacity-90',
    secondary: 'bg-surface-2 text-text hover:bg-border',
    ghost: 'text-muted hover:text-text hover:bg-surface-2',
    danger: 'bg-danger-soft text-danger hover:opacity-90',
  }[variant]
  const pad = size === 'sm' ? 'px-2.5 py-1.5 text-sm' : 'px-4 py-2.5'
  return (
    <button
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-medium',
        'transition-colors disabled:opacity-40 disabled:pointer-events-none',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        styles, pad, className,
      )}
      {...rest}
    />
  )
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('bg-surface border border-border rounded-2xl', className)}>
      {children}
    </div>
  )
}

export function Badge({
  children, tone = 'neutral', className,
}: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent'; className?: string }) {
  const tones = {
    neutral: 'bg-surface-2 text-muted',
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    accent: 'bg-accent-soft text-accent',
  }[tone]
  return (
    <span className={cx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', tones, className)}>
      {children}
    </span>
  )
}

export function Field({
  label, hint, children, className,
}: { label: string; hint?: string; children: ReactNode; className?: string }) {
  /*
   * `min-w-0` ist hier kein Feinschliff, sondern die Reparatur eines echten Fehlers.
   *
   * Ein Element in einem Grid oder Flex schrumpft von sich aus NICHT unter die
   * Eigenbreite seines Inhalts (`min-width: auto`). Ein `<input type="date">` bringt
   * in Safari eine solche Eigenbreite mit — die des ausgeschriebenen Datums plus
   * Kalendersymbol. In `grid-cols-2` war das breiter als die Spalte, und das Feld
   * „Abreise" lief auf dem iPhone über den rechten Rand des Blattes hinaus.
   *
   * Am Feld statt am Aufrufer, weil sonst jede künftige Zweispaltigkeit denselben
   * Fehler neu macht — und ihn erst jemand auf einem Telefon bemerkt.
   */
  return (
    <label className={cx('block min-w-0', className)}>
      <span className="block text-xs font-medium text-muted mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted mt-1">{hint}</span>}
    </label>
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cx(
        // `min-w-0` aus demselben Grund wie am Feld: `w-full` allein genügt nicht,
        // solange die Eigenbreite des Inhalts die Untergrenze setzt.
        'w-full min-w-0 bg-surface-2 border border-border rounded-xl px-3 py-2.5',
        'focus:outline-2 focus:outline-accent focus:outline-offset-0',
        className,
      )}
      {...rest}
    />
  )
}

export function Select({
  className, children, ...rest
}: InputHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select
      className={cx(
        // Dasselbe wie beim Eingabefeld: ein Auswahlfeld ist so breit wie sein
        // längster Eintrag, und „partially_retained" ist länger als eine halbe
        // Handybreite.
        'w-full min-w-0 bg-surface-2 border border-border rounded-xl px-3 py-2.5',
        'focus:outline-2 focus:outline-accent', className,
      )}
      {...rest}
    >
      {children}
    </select>
  )
}

/**
 * Bottom sheet on a phone, side panel on a desktop — one component, because the
 * whole app is meant to be driven with a thumb.
 */
export function Sheet({
  open, onClose, title, children, footer,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex sm:items-center sm:justify-end items-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cx(
          'relative w-full sm:w-[32rem] bg-surface border-border shadow-2xl',
          // Full height on a phone rather than a 92vh bottom sheet. iOS does not
          // resize the layout viewport for the keyboard, it scrolls the page — and a
          // fixed overlay that does not reach the bottom edge visibly floats up and
          // down while typing. No gap, no float.
          'h-dvh sm:h-full sm:max-h-full flex flex-col',
          'sm:rounded-none border-t sm:border-t-0 sm:border-l',
        )}
      >
        {/*
          * `sheet-kopf` trägt in der nativen Hülle den sicheren Rand nach oben.
          *
          * Das Sheet ist `fixed inset-0` und `h-dvh` — es liegt über allem und
          * damit AUSSERHALB von `.app-schale`, wo der obere Rand sonst gesetzt
          * wird. Ohne eigene Regel liegt die Überschrift unter Statusleiste und
          * Dynamic Island. Am 31.07.2026 auf echter Hardware über TestFlight
          * gefunden — im Browser tritt es nicht auf, dort schiebt Safaris eigene
          * Leiste den Inhalt herunter, und im Simulator war es nur zu sehen, wenn
          * man die App und nicht Safari geöffnet hatte.
          */}
        <header className="sheet-kopf flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
          <div className="min-w-0 flex-1 font-semibold truncate">{title}</div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Schließen">
            <X size={18} />
          </Button>
        </header>
        {/*
          * `overflow-x-hidden` ist hier kein Feinschliff, sondern eine Sperre.
          *
          * `overflow-y: auto` allein gibt es in CSS nicht: steht eine Achse auf
          * `auto` und die andere auf `visible`, rechnet der Browser die andere
          * ebenfalls auf `auto` herunter. Dieser Blattkörper war damit in JEDEM
          * Sheet seitlich scrollbar — und sobald irgendein Kind auch nur wenige
          * Pixel zu breit wird, lässt sich der ganze Inhalt nach links und rechts
          * schieben und bleibt dann verschoben stehen. Auf dem Telefon sieht das
          * aus, als schwimme das Fenster.
          *
          * Abgeschnitten wird dadurch nichts: in WebKit bei 320, 390 und 440 px
          * nachgemessen, kein Element in einem Sheet überschreitet die Breite.
          * Wer hier künftig etwas unterbringt, das WIRKLICH breiter sein muss —
          * eine breite Tabelle etwa —, gibt ihm einen eigenen `overflow-x-auto`
          * Container. Genau ein Ding scrollt dann seitlich, nicht das ganze Blatt.
          */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">{children}</div>
        {footer && (
          <footer className="border-t border-border p-3 pb-safe shrink-0 flex gap-2">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}

export function EmptyState({ icon, title, hint }: { icon?: ReactNode; title: string; hint?: string }) {
  return (
    <div className="text-center py-12 px-6">
      {icon && <div className="text-muted mb-3 flex justify-center">{icon}</div>}
      <p className="font-medium">{title}</p>
      {hint && <p className="text-sm text-muted mt-1">{hint}</p>}
    </div>
  )
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className={cx('text-2xl font-semibold mt-1 tabular-nums', tone)}>{value}</div>
    </Card>
  )
}
